import { Doctor, BPDoctor, Consult } from "../models";
import { differenceInHours } from "date-fns/differenceInHours";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { parseISO } from "date-fns/parseISO";
import { fromZonedTime } from "date-fns-tz";
import { addHours } from "date-fns/addHours";
import { addMinutes } from "date-fns/addMinutes";

export const formatCentreName = (name: string) => {
	if (!name) {
		return "";
	}
	return name.replace(/our medical/gi, "").trim();
};

export const setGender = (gender: string) => {
	switch (gender) {
		case "M":
			return "Male";
		case "F":
			return "Female";
		case null:
			return "";
		default:
			return gender;
	}
};

export const mapDoctorInstance = (bpDoctor: BPDoctor): BPDoctor => {
	return {
		...bpDoctor,
		clinicName: formatCentreName(bpDoctor.clinicName),
		gender: setGender(bpDoctor.gender)
	};
};

export const mapDoctor = (doctor: Doctor): Doctor => {
	if (!doctor) {
		return {} as Doctor;
	}
	return {
		...doctor,
		lthDailyCap: doctor.lthDailyCap ?? null,
		bpDoctors: doctor.doctorInstances.map((di) => mapDoctorInstance(di))
	};
};

export const formatTime = (value: string) => {
	if (!value) {
		return "";
	}
	let result = value;
	if (result.length === 8 && result.lastIndexOf(":") === 5) {
		result = result.substring(0, 5);
	}
	if (result.startsWith("0")) {
		result = result.substring(1);
	}
	return result;
};

export const convertLocalDateToUTCDate = (localDateString: string) => {
	if (localDateString == null || localDateString === "") {
		return "";
	}

	const today = new Date();
	const timezoneOffsetMinutes = today.getTimezoneOffset();
	const hoursoffset = Math.floor(timezoneOffsetMinutes / 60);
	const minutesOffset = timezoneOffsetMinutes - hoursoffset * 60;
	const localDate = parse(localDateString, "yyyy-MM-dd HH:mm", new Date());
	const utcDate = addMinutes(addHours(localDate, hoursoffset), minutesOffset);
	// console.info(
	//   "convertLocalDateToUTCDate ",
	//   format(localDate, "dd/MM/yyyy HH:mm"),
	//   format(utcDate, "dd/MM/yyyy HH:mm")
	// );
	return `${format(utcDate, "yyyy-MM-dd")}T${format(utcDate, "HH:mm:ss")}`;
};

export function localToUtcString(input: string): string {
	if (!input) {
		throw new Error("Input date is required");
	}

	let parsedDate: Date;

	if (input.includes("T")) {
		parsedDate = parse(input, "yyyy-MM-dd'T'HH:mm:ss", new Date());
	} else {
		parsedDate = parse(input, "yyyy-MM-dd", new Date());
	}

	if (isNaN(parsedDate.getTime())) {
		throw new Error("Invalid date format");
	}

	const utcDate = fromZonedTime(
		parsedDate,
		Intl.DateTimeFormat().resolvedOptions().timeZone
	);
	console.info("localToUtcString", input, utcDate.toISOString());

	return utcDate.toISOString();
}

export const convertUTCDateToLocalDate = (utcDateString: string) => {
	if (utcDateString == null) {
		return "";
	}
	const utcDate = parseISO(utcDateString);
	const today = new Date();
	const timezoneOffsetMinutes = today.getTimezoneOffset();
	const hoursoffset = Math.floor(timezoneOffsetMinutes / 60);
	const minutesOffset = timezoneOffsetMinutes - hoursoffset * 60;
	const localDate = addMinutes(addHours(utcDate, -hoursoffset), -minutesOffset);
	// console.info(
	// 	"convertArrivalTimeToDateTime ",
	// 	timezoneOffsetMinutes,
	// 	hoursoffset,
	// 	minutesOffset,
	// 	utcDateString,
	// 	format(utcDate, "dd/MM/yyyy HH:mm"),
	// 	format(localDate, "dd/MM/yyyy HH:mm"),
	// );
	return localDate;
};

export const calculateTelehealthWaitTime = (
	telehealthArrival?: string | null
) => {
	if (!telehealthArrival) {
		return "–";
	}

	// The API may omit the timezone suffix on this UTC timestamp.
	const timestamp = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(telehealthArrival)
		? telehealthArrival
		: `${telehealthArrival}Z`;
	return `${differenceInMinutes(new Date(), parseISO(timestamp))} mins`;
};

export const calculateWaitTime = (arrivalDateTime?: Date) => {
	if (arrivalDateTime == null) {
		return "";
	}

	const hours = differenceInHours(new Date(), arrivalDateTime);
	const minutes = differenceInMinutes(new Date(), arrivalDateTime) - 60 * hours;
	let result = "";
	if (hours > 0) {
		result = `${hours} hrs`;
	}
	if (minutes > 0) {
		result += ` ${minutes} mins`;
	}
	if (result === "") {
		result += `0 mins`;
	}
	// console.info(
	//   "calculateWaitTime ",
	//   format(arrivalDateTime, "dd/MM/yyyy HH:mm"),
	//   hours,
	//   minutes,
	//   result
	// );
	return result.trim();
};

export const formatPhone = (number: string) => {
	if (!number) {
		return "";
	}
	if (number.length !== 10) {
		return number;
	}
	return `${number.substring(0, 4)} ${number.substring(
		4,
		7
	)} ${number.substring(7, 10)}`;
};

export const formatMedicare = (number: string) => {
	if (!number) {
		return "";
	}
	if (number.length !== 10) {
		return number;
	}
	return `${number.substring(0, 4)} ${number.substring(
		4,
		9
	)} ${number.substring(9, 10)}`;
};

export const mapConsult = (consult: Consult) => {
	return {
		...consult,
		dateOfBirth: consult.dateOfBirth
			? format(
					parse(consult.dateOfBirth, "yyyy-MM-dd", new Date()),
					"dd/MM/yyyy"
				)
			: "",
		clinicName: formatCentreName(consult.clinicName),
		patientPhone: formatPhone(consult.patientPhone?.trim()),
		arrivalDateTime: parseISO(consult.arrival),
		telehealthArrivalDateTime: consult.telehealthArrival
			? convertUTCDateToLocalDate(consult.telehealthArrival)
			: null,
		assignedDateTime: consult.assignedTime
			? convertUTCDateToLocalDate(consult.assignedTime)
			: null,
		endDateTime: consult.end_time ? parseISO(consult.end_time) : null
	} as Consult;
};

export const isTestEnvironment = () => {
	return (process.env.REACT_APP_IS_TEST_ENV ?? "false") === "true";
};
